// Tag color utility for consistent tag coloring across the application
// Uses a hash-based approach to ensure the same tag always gets the same color

/**
 * Available tag color variants with their corresponding Tailwind classes
 */
const TAG_COLOR_VARIANTS = [
  {
    name: 'blue',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    darkBg: 'dark:bg-blue-900/20',
    darkText: 'dark:text-blue-300',
    darkBorder: 'dark:border-blue-800',
    hoverBg: 'hover:bg-blue-200',
    darkHoverBg: 'dark:hover:bg-blue-900/30',
  },
  {
    name: 'green',
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    darkBg: 'dark:bg-green-900/20',
    darkText: 'dark:text-green-300',
    darkBorder: 'dark:border-green-800',
    hoverBg: 'hover:bg-green-200',
    darkHoverBg: 'dark:hover:bg-green-900/30',
  },
  {
    name: 'purple',
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-200',
    darkBg: 'dark:bg-purple-900/20',
    darkText: 'dark:text-purple-300',
    darkBorder: 'dark:border-purple-800',
    hoverBg: 'hover:bg-purple-200',
    darkHoverBg: 'dark:hover:bg-purple-900/30',
  },
  {
    name: 'orange',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200',
    darkBg: 'dark:bg-orange-900/20',
    darkText: 'dark:text-orange-300',
    darkBorder: 'dark:border-orange-800',
    hoverBg: 'hover:bg-orange-200',
    darkHoverBg: 'dark:hover:bg-orange-900/30',
  },
  {
    name: 'pink',
    bg: 'bg-pink-100',
    text: 'text-pink-800',
    border: 'border-pink-200',
    darkBg: 'dark:bg-pink-900/20',
    darkText: 'dark:text-pink-300',
    darkBorder: 'dark:border-pink-800',
    hoverBg: 'hover:bg-pink-200',
    darkHoverBg: 'dark:hover:bg-pink-900/30',
  },
  {
    name: 'indigo',
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
    border: 'border-indigo-200',
    darkBg: 'dark:bg-indigo-900/20',
    darkText: 'dark:text-indigo-300',
    darkBorder: 'dark:border-indigo-800',
    hoverBg: 'hover:bg-indigo-200',
    darkHoverBg: 'dark:hover:bg-indigo-900/30',
  },
  {
    name: 'teal',
    bg: 'bg-teal-100',
    text: 'text-teal-800',
    border: 'border-teal-200',
    darkBg: 'dark:bg-teal-900/20',
    darkText: 'dark:text-teal-300',
    darkBorder: 'dark:border-teal-800',
    hoverBg: 'hover:bg-teal-200',
    darkHoverBg: 'dark:hover:bg-teal-900/30',
  },
  {
    name: 'amber',
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-200',
    darkBg: 'dark:bg-amber-900/20',
    darkText: 'dark:text-amber-300',
    darkBorder: 'dark:border-amber-800',
    hoverBg: 'hover:bg-amber-200',
    darkHoverBg: 'dark:hover:bg-amber-900/30',
  },
  {
    name: 'emerald',
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    border: 'border-emerald-200',
    darkBg: 'dark:bg-emerald-900/20',
    darkText: 'dark:text-emerald-300',
    darkBorder: 'dark:border-emerald-800',
    hoverBg: 'hover:bg-emerald-200',
    darkHoverBg: 'dark:hover:bg-emerald-900/30',
  },
  {
    name: 'rose',
    bg: 'bg-rose-100',
    text: 'text-rose-800',
    border: 'border-rose-200',
    darkBg: 'dark:bg-rose-900/20',
    darkText: 'dark:text-rose-300',
    darkBorder: 'dark:border-rose-800',
    hoverBg: 'hover:bg-rose-200',
    darkHoverBg: 'dark:hover:bg-rose-900/30',
  },
];

/**
 * Simple hash function to convert a string to a number
 * This ensures the same string always produces the same hash
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get consistent color classes for a tag based on its name
 * The same tag name will always return the same color
 */
export function getTagColor(tagName: string) {
  const hash = hashString(tagName.toLowerCase());
  const colorIndex = hash % TAG_COLOR_VARIANTS.length;
  return TAG_COLOR_VARIANTS[colorIndex];
}

/**
 * Get the complete className string for a tag badge
 * This combines all the color classes into a single string
 */
export function getTagClassName(tagName: string, size: 'sm' | 'md' = 'sm'): string {
  const color = getTagColor(tagName);
  const baseClasses = size === 'sm' ? 'text-[11px]' : 'text-xs';
  
  // Use a more explicit approach to ensure classes are included
  const colorClasses = [
    color.bg,
    color.text,
    color.border,
    color.darkBg,
    color.darkText,
    color.darkBorder,
    color.hoverBg,
    color.darkHoverBg
  ].join(' ');
  
  return `${baseClasses} font-medium ${colorClasses} border transition-colors cursor-pointer`;
}

/**
 * Get button-specific classes for filter tags
 */
export function getFilterTagClassName(tagName: string, isActive: boolean): string {
  const color = getTagColor(tagName);
  const baseClasses = 'px-3 h-7 rounded-full border text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1';
  
  if (isActive) {
    // Active state: full color with shadow
    const activeColorClasses = [
      color.bg,
      color.text,
      color.border,
      color.darkBg,
      color.darkText,
      color.darkBorder,
      'shadow-sm'
    ].join(' ');
    
    return `${baseClasses} ${activeColorClasses}`;
  } else {
    // Inactive state: lighter version of the tag's color with hover effect
    const inactiveColorClasses = [
      'bg-white',
      color.text,
      color.border,
      color.darkBg,
      color.darkText,
      color.darkBorder,
      `hover:${color.bg}`
    ].join(' ');
    
    return `${baseClasses} ${inactiveColorClasses}`;
  }
}

/**
 * Get search facet button classes
 */
export function getSearchFacetClassName(tagName: string): string {
  const color = getTagColor(tagName);
  const baseClasses = 'inline-flex items-center gap-1 px-2 py-1 border rounded-md text-xs transition-all duration-200 hover:opacity-80';
  const colorClasses = [
    color.bg,
    color.text,
    color.border,
    color.darkBg,
    color.darkText,
    color.darkBorder
  ].join(' ');
  
  return `${baseClasses} ${colorClasses}`;
}

/**
 * Get individual color properties for custom styling
 */
export function getTagColorProperties(tagName: string) {
  return getTagColor(tagName);
}